/**
 * LanguageLessonView (Sprint 29 — Language course Pha 1) — renderer tương tác
 * cho bài học ngôn ngữ (schema importer `language_import.py`).
 *
 * Hiển thị khi `lesson.structured_payload` có dữ liệu; nếu null thì
 * LessonDetail fallback về <MarkdownContent>. Đầy đủ tương tác:
 *   - Toggle hiển thị Furigana / Romaji / Cả hai cho mẫu câu + từ vựng.
 *   - Thẻ từ vựng dạng lưới, nút phát âm (placeholder — Pha 2 nối TTS→R2).
 *   - Flashcard SRS lật mặt (click).
 *   - Ngữ pháp, tình huống (hook), kanji, luyện nói, văn hoá.
 *
 * Quiz nghe (listening) KHÔNG render ở đây — giữ luồng Quiz riêng (đã có).
 */

import { useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Divider,
  Flex,
  Segmented,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  BulbOutlined,
  GlobalOutlined,
  SoundOutlined,
} from "@ant-design/icons";
import type { LangSrsCard, LanguageLessonPayload } from "@/api/catalog";
import { SpeakingPractice } from "@/components/SpeakingPractice";

const { Title, Paragraph, Text } = Typography;

type Reading = "furigana" | "romaji" | "both";

/**
 * Nút phát âm. Có `src` → phát file audio TTS (Pha 2). Không có → disabled
 * (bài chưa sinh audio). Click dừng audio đang phát rồi phát mới.
 */
function AudioButton({ src, label }: { src?: string; label?: string }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const play = () => {
    if (!src) return;
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio(src);
      }
      audioRef.current.currentTime = 0;
      void audioRef.current.play();
    } catch {
      // Bỏ qua lỗi phát (vd autoplay policy) — không chặn UI.
    }
  };

  if (!src) {
    return (
      <Tooltip title="Bài này chưa có audio">
        <Button
          size="small"
          type="text"
          disabled
          icon={<SoundOutlined />}
          aria-label={label ? `Phát âm: ${label}` : "Phát âm"}
        />
      </Tooltip>
    );
  }

  return (
    <Button
      size="small"
      type="text"
      icon={<SoundOutlined />}
      onClick={play}
      aria-label={label ? `Phát âm: ${label}` : "Phát âm"}
    />
  );
}

interface Props {
  payload: LanguageLessonPayload;
  /** Pha 3 — chấm nhớ thẻ SRS (Quên=1, Tạm=3, Dễ=5). Có → hiện nút chấm khi lật thẻ. */
  onGrade?: (card: LangSrsCard, quality: number) => void;
  /** Pha 3 — slug bài để luyện nói (consent + ghi âm chấm). Thiếu → phần nói tĩnh. */
  lessonSlug?: string;
}

export function LanguageLessonView({ payload, onGrade, lessonSlug }: Props) {
  const [reading, setReading] = useState<Reading>("furigana");
  const [flipped, setFlipped] = useState<Record<number, boolean>>({});

  const showFurigana = reading === "furigana" || reading === "both";
  const showRomaji = reading === "romaji" || reading === "both";

  const {
    title_vi,
    title_ja,
    level,
    duration_min,
    grammar_point,
    hook,
    model_sentences = [],
    vocab = [],
    kanji = [],
    speaking = [],
    culture_note,
    srs_cards = [],
  } = payload;

  return (
    <div data-testid="language-lesson-view">
      {/* Header */}
      <Space direction="vertical" size={4} style={{ width: "100%" }}>
        {title_vi && (
          <Title level={3} style={{ marginBottom: 0 }}>
            {title_vi}
          </Title>
        )}
        <Space wrap>
          {title_ja && <Text type="secondary">{title_ja}</Text>}
          {level && <Tag color="geekblue">{level}</Tag>}
          {duration_min ? <Tag>{duration_min} phút</Tag> : null}
        </Space>
      </Space>

      {/* Ngữ pháp */}
      {grammar_point && (grammar_point.form || grammar_point.meaning_vi) && (
        <Card
          size="small"
          title={
            <Space>
              <BulbOutlined />
              Ngữ pháp
            </Space>
          }
          style={{ marginTop: 16 }}
          data-testid="lang-grammar"
        >
          {grammar_point.form && (
            <Title level={4} style={{ marginTop: 0 }}>
              {grammar_point.form}
            </Title>
          )}
          {grammar_point.meaning_vi && (
            <Paragraph style={{ marginBottom: 8 }}>
              {grammar_point.meaning_vi}
            </Paragraph>
          )}
          {grammar_point.note_vi && (
            <Text type="secondary">{grammar_point.note_vi}</Text>
          )}
        </Card>
      )}

      {/* Tình huống (hook) */}
      {hook && (hook.scene_vi || hook.audio_script_ja) && (
        <Card size="small" title="Tình huống" style={{ marginTop: 16 }}>
          {hook.scene_vi && (
            <Paragraph style={{ marginBottom: 8 }}>{hook.scene_vi}</Paragraph>
          )}
          {hook.audio_script_ja && (
            <Flex align="center" gap={8}>
              <AudioButton label={hook.audio_script_ja} />
              <Text style={{ fontSize: 16 }}>{hook.audio_script_ja}</Text>
            </Flex>
          )}
        </Card>
      )}

      {/* Toggle hiển thị */}
      {(model_sentences.length > 0 || vocab.length > 0) && (
        <Flex justify="flex-end" style={{ marginTop: 16 }}>
          <Segmented
            value={reading}
            onChange={(v) => setReading(v as Reading)}
            options={[
              { label: "Furigana", value: "furigana" },
              { label: "Romaji", value: "romaji" },
              { label: "Cả hai", value: "both" },
            ]}
            data-testid="lang-reading-toggle"
          />
        </Flex>
      )}

      {/* Mẫu câu */}
      {model_sentences.length > 0 && (
        <Card size="small" title="Mẫu câu" style={{ marginTop: 8 }}>
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            {model_sentences.map((s, i) => (
              <div key={i} data-testid="lang-model-sentence">
                <Flex align="center" gap={8}>
                  <AudioButton src={s.audio} label={s.ja} />
                  <Text strong style={{ fontSize: 18 }}>
                    {showFurigana ? s.furigana || s.ja : s.ja}
                  </Text>
                </Flex>
                {showRomaji && s.romaji && (
                  <Text type="secondary" style={{ marginLeft: 32 }}>
                    {s.romaji}
                  </Text>
                )}
                {s.vi && (
                  <Paragraph style={{ marginLeft: 32, marginBottom: 0 }}>
                    {s.vi}
                  </Paragraph>
                )}
                {i < model_sentences.length - 1 && (
                  <Divider style={{ margin: "8px 0" }} />
                )}
              </div>
            ))}
          </Space>
        </Card>
      )}

      {/* Từ vựng */}
      {vocab.length > 0 && (
        <Card size="small" title="Từ vựng" style={{ marginTop: 16 }}>
          <Flex wrap gap={12}>
            {vocab.map((v, i) => (
              <Card
                key={i}
                size="small"
                style={{ width: 200 }}
                data-testid="lang-vocab-card"
              >
                <Flex align="center" justify="space-between">
                  <Text strong style={{ fontSize: 18 }}>
                    {v.ja}
                  </Text>
                  <AudioButton src={v.audio} label={v.ja} />
                </Flex>
                {showFurigana && v.furigana && (
                  <div>
                    <Text type="secondary">{v.furigana}</Text>
                  </div>
                )}
                {showRomaji && v.romaji && (
                  <div>
                    <Text type="secondary" italic>
                      {v.romaji}
                    </Text>
                  </div>
                )}
                {v.vi && <div>{v.vi}</div>}
                {v.pos && (
                  <Tag style={{ marginTop: 4 }} color="default">
                    {v.pos}
                  </Tag>
                )}
              </Card>
            ))}
          </Flex>
        </Card>
      )}

      {/* Kanji */}
      {kanji.length > 0 && (
        <Card size="small" title="Kanji" style={{ marginTop: 16 }}>
          <Flex wrap gap={12}>
            {kanji.map((k, i) => (
              <Card key={i} size="small" style={{ width: 150 }}>
                <Title level={2} style={{ margin: 0, textAlign: "center" }}>
                  {k.char}
                </Title>
                {k.on && (
                  <div>
                    <Text type="secondary">On: {k.on}</Text>
                  </div>
                )}
                {k.kun && (
                  <div>
                    <Text type="secondary">Kun: {k.kun}</Text>
                  </div>
                )}
                {k.vi && <div>{k.vi}</div>}
              </Card>
            ))}
          </Flex>
        </Card>
      )}

      {/* Luyện nói — có lessonSlug → ghi âm chấm phát âm (consent gate); else tĩnh */}
      {speaking.length > 0 &&
        (lessonSlug ? (
          <SpeakingPractice lessonSlug={lessonSlug} speaking={speaking} />
        ) : (
          <Card size="small" title="Luyện nói" style={{ marginTop: 16 }}>
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              {speaking.map((s, i) => (
                <div key={i} data-testid="lang-speaking">
                  {s.prompt_vi && <Text>{s.prompt_vi}</Text>}
                  {s.target_ja && (
                    <Flex align="center" gap={8}>
                      <AudioButton src={s.audio} label={s.target_ja} />
                      <Text strong>{s.target_ja}</Text>
                    </Flex>
                  )}
                </div>
              ))}
            </Space>
          </Card>
        ))}

      {/* Văn hoá */}
      {culture_note?.vi && (
        <Alert
          style={{ marginTop: 16 }}
          type="info"
          showIcon
          icon={<GlobalOutlined />}
          message="Văn hoá"
          description={culture_note.vi}
        />
      )}

      {/* Thẻ ôn tập SRS — click để lật; có onGrade thì hiện nút chấm nhớ */}
      {srs_cards.length > 0 && (
        <Card
          size="small"
          title={onGrade ? "Thẻ ôn tập — lật rồi tự chấm" : "Thẻ ôn tập (bấm để lật)"}
          style={{ marginTop: 16 }}
        >
          {onGrade && (
            <Paragraph type="secondary" style={{ marginBottom: 12, fontSize: 13 }}>
              Bấm vào thẻ để lật xem nghĩa, rồi tự chấm mức nhớ — hệ thống sẽ tự xếp
              lịch ôn lại (lặp lại spaced repetition):
              <br />
              <Text type="secondary">
                • <Text strong>Chưa nhớ</Text>: ôn lại sớm (ngày mai) ·{" "}
                <Text strong>Nhớ</Text>: giãn cách vừa ·{" "}
                <Text strong>Dễ</Text>: giãn cách dài (lâu mới phải ôn lại).
              </Text>
            </Paragraph>
          )}
          <Flex wrap gap={12}>
            {srs_cards.map((c, i) => (
              <Card
                key={i}
                hoverable
                size="small"
                onClick={() => setFlipped((f) => ({ ...f, [i]: !f[i] }))}
                style={{ width: 170, textAlign: "center", cursor: "pointer" }}
                data-testid="lang-srs-card"
              >
                <Text strong style={{ fontSize: 16 }}>
                  {flipped[i] ? c.back_vi : c.front_ja}
                </Text>
                {onGrade && flipped[i] && (
                  <Flex gap={4} wrap justify="center" style={{ marginTop: 8 }}>
                    {[
                      { label: "Chưa nhớ", q: 1 },
                      { label: "Nhớ", q: 3 },
                      { label: "Dễ", q: 5 },
                    ].map(({ label, q }) => (
                      <Button
                        key={q}
                        size="small"
                        data-testid={`srs-grade-${q}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onGrade(c, q);
                          setFlipped((f) => ({ ...f, [i]: false }));
                        }}
                      >
                        {label}
                      </Button>
                    ))}
                  </Flex>
                )}
              </Card>
            ))}
          </Flex>
        </Card>
      )}
    </div>
  );
}
